// The waveform renderer (P2.6) — UX §3.1's ribbon and overview, drawn from P2.4's mipmaps.
//
// **Draw is dumb, and that is the roadmap's own mitigation for waveform performance rather than a
// stylistic preference.** All the cleverness happened once, at load, in the analysis worker. What
// happens per frame here is: read one float from a SharedArrayBuffer, set five uniforms, issue two
// draw calls. There is no per-pixel JavaScript, no vertex buffer rebuilt per frame, and nothing
// that scales with track length — a 20-minute set costs exactly what a 20-second one does.
//
// The samples are already on the GPU. Each mipmap level is uploaded **once**, as a texture, when
// the track arrives; scrolling the ribbon changes a uniform, not a buffer. That is what makes
// Architecture §4.4's "waveform rendering never touches PCM at draw time" true in the strong sense
// — at draw time it does not touch the *mipmaps* either.
//
// Implements `Renderer` (Architecture §6.1) so `RenderLoop` drives it and writes the same
// `RENDER_STATS` layout the scene does. That is not tidiness: P2.6's verify is "ribbon holds 60 FPS
// at 1080p with the scene also running (`/bench` measures this)", and reusing the loop is what lets
// one bench read both displays with one function instead of growing a second statistics format.

import {
  RIBBON_VISIBLE_SECONDS,
  layoutFor,
  type WaveformGrid,
  type WaveformLaneLayout,
  type WaveformOrientation,
  type WaveformTheme,
} from '../../platform/types/waveform-render';
import {
  computeWaveformMipmapLayout,
  levelForFramesPerPixel,
  WAVEFORM_BUCKET_FLOATS,
  type WaveformMipmapLevel,
} from '../../platform/types/waveform-mipmap';
import { SignalReader } from '../../platform/sab/signal-bus';
import { SIGNAL_SOURCE, type SignalSourceIndex } from '../../platform/types/rings';
import type { Renderer, RendererInit } from '../core/renderer';

/**
 * Widest texture row we will ask for.
 *
 * `MAX_TEXTURE_SIZE` is at least 2048 everywhere WebGL2 exists and is usually 16384, but a wide
 * single-row texture is not the goal — wrapping into rows costs one integer divide in the shader
 * and keeps us far away from the driver-specific limits that make a texture allocation fail at
 * runtime on somebody else's machine and nowhere near ours.
 */
const MAX_TEXTURE_WIDTH = 4096;

/**
 * Beats to a bar, for the brighter grid line.
 *
 * Four, and v1 is not entitled to any other answer: nothing in the analysis pipeline detects a time
 * signature, so this is a convention rather than a measurement. It only ever applies when
 * `WaveformGrid.showBars` is set, which happens when a human placed the anchor themselves.
 */
const BEATS_PER_BAR = 4;

export const VERTEX_SHADER = `#version 300 es
// A full-viewport triangle pair, generated from gl_VertexID. No vertex buffer, no VAO state to
// get wrong, nothing to rebind between the two draws.
out vec2 vUv;
void main() {
  vec2 corner = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = corner;
  gl_Position = vec4(corner * 2.0 - 1.0, 0.0, 1.0);
}`;

export const FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform sampler2D uBuckets;
uniform ivec2 uTexSize;
uniform int uBucketCount;
// The visible window, in buckets. Fractional, so scrolling is smooth rather than snapping a
// bucket at a time — the difference between a ribbon that glides and one that stutters.
uniform float uFirstBucket;
uniform float uVisibleBuckets;
uniform vec3 uEnvelope;
uniform vec3 uCore;
uniform vec3 uPlayhead;
/** 0..1 across the viewport; negative means "no playhead on this display". */
uniform float uPlayheadX;
uniform float uPlayheadHalfWidth;

// Beat ticks (P3.13) — UX 3.1's "beat ticks", drawn from the grid the deck is actually using,
// which since P3.12 may be a human's correction. A uBeatBuckets of 0 means "no grid": the ribbon
// then draws none, which is the honest rendering of not knowing and is distinguishable from ticks
// in the wrong place. Both are in *bucket* units so the fragment needs no time conversion.
uniform float uBeatBuckets;
uniform float uAnchorBucket;
uniform float uBarBeats;

in vec2 vUv;
out vec4 outColor;

void main() {
  if (uPlayheadX >= 0.0 && abs(vUv.x - uPlayheadX) <= uPlayheadHalfWidth) {
    outColor = vec4(uPlayhead, 1.0);
    return;
  }

  float bucket = uFirstBucket + vUv.x * uVisibleBuckets;
  int index = int(floor(bucket));
  // Before the start or past the end of the track. Transparent rather than clamped: a waveform
  // that repeats its first bucket for the two seconds before a track begins looks like audio.
  if (index < 0 || index >= uBucketCount) {
    outColor = vec4(0.0);
    return;
  }

  vec4 summary = texelFetch(uBuckets, ivec2(index % uTexSize.x, index / uTexSize.x), 0);
  float lo = summary.r;
  float hi = summary.g;
  float rms = summary.b;

  // vUv.y is 0 at the bottom; the waveform is drawn around a centre line at 0.
  float y = (vUv.y - 0.5) * 2.0;

  // The familiar bright core inside a dimmer envelope: RMS is perceived loudness, min/max is the
  // transient reach. Drawing only one of them loses either the punch or the body.
  vec4 wave = (y < lo || y > hi)
    ? vec4(0.0)
    : (abs(y) <= rms ? vec4(uCore, 1.0) : vec4(uEnvelope, 1.0));

  // A beat tick, composited **over** the waveform rather than instead of it.
  //
  // Mixed in here rather than returned early, and the difference is visible: an early return paints
  // the tick onto the cleared background and punches a transparent gap through the waveform at
  // exactly the column the user is trying to read. The whole question a grid line answers is
  // "does this line fall on that transient", so the transient has to survive the line.
  if (uBeatBuckets > 0.0) {
    float beats = (bucket - uAnchorBucket) / uBeatBuckets;
    // Distance to the *nearest* beat. A fract() alone would only measure to the beat behind, making
    // every tick one-sided and half as easy to aim at.
    float toNearest = abs(beats - floor(beats + 0.5)) * uBeatBuckets;

    // One device pixel, in bucket units. uPlayheadHalfWidth is 1/pixelWidth, so this is buckets
    // per pixel — the conversion that lets the widths below be stated in pixels, which is the only
    // unit a legibility decision means anything in. The mipmap level is chosen so this lands near 1
    // (see levelForFramesPerPixel), but stating it explicitly keeps the line the same thickness
    // if that ever changes.
    float pixelBuckets = max(0.5, uVisibleBuckets * uPlayheadHalfWidth);

    // ⚠️ **An outlined line, not a brighter one, and the reason is a contrast measurement rather
    // than taste.** The tick draws in uPlayhead — --text-hi, near-white at (0.93, 0.94, 1.00).
    // The RMS core it has to be read against is the deck colour lifted towards white: deck A's is
    // (1.00, 0.71, 0.79). Those two are close enough that *no* mix strength separates them — the
    // previous 0.55 landed on (0.97, 0.84, 0.90) against a core of (1.00, 0.71, 0.79), and pushing
    // it to 1.0 only trades an invisible line for a slightly less invisible one. P3.13 had already
    // raised this from 0.4 for exactly this complaint and got exactly this far.
    //
    // A dark edge either side fixes it without touching the palette, because the edge is dark where
    // the waveform is bright and the centre is bright where the background is dark. The tick is
    // therefore legible over the core, over the envelope, and over the empty lane — and it stays
    // legible under any theme, which a hand-tuned brightness would not.
    //
    // Reported by the stakeholder on 4 Sep: "too faint to read at 1536 px, which undercuts the
    // whole 'do they land on the kicks' check" — and that check is the one FR-13 exists for.
    float coreHalf = pixelBuckets;
    float edgeHalf = coreHalf + pixelBuckets;
    if (toNearest <= edgeHalf) {
      float nearestBeat = floor(beats + 0.5);
      // Every fourth beat from the anchor is a bar line. Brighter, not wider: a wider line reads as
      // a different kind of thing, and at ribbon zoom it would swallow the beats either side.
      bool bar = uBarBeats > 0.0 &&
        abs(nearestBeat / uBarBeats - floor(nearestBeat / uBarBeats + 0.5)) < 0.001;
      // Four device pixels all told — one dark, two bright, one dark. The transient still survives
      // it: at ribbon zoom a beat is some ninety pixels wide, so this covers 4% of one.
      wave = toNearest <= coreHalf
        ? mix(wave, vec4(uPlayhead, 1.0), bar ? 1.0 : 0.92)
        : mix(wave, vec4(0.0, 0.0, 0.0, 1.0), bar ? 0.7 : 0.55);
    }
  }

  outColor = wave;
}`;

/**
 * Every uniform the program sets, by name.
 *
 * ⚠️ **Adding a uniform to the shader and forgetting this list fails silently**, which is exactly
 * how P3.13's beat ticks shipped drawing nothing: `getUniformLocation` is never called for a name
 * that is not here, `#uniforms[name]` comes back `undefined`, the `?? null` turns the write into a
 * documented no-op, and the shader reads its own default of `0.0` forever. No exception, no warning,
 * no visual clue beyond the feature not existing.
 *
 * `uniformNamesIn` below extracts the declarations from the shader source, and a unit test compares
 * the two — in Node, with no GL context, because the failure is a *string* mismatch and does not
 * need a GPU to detect.
 */
export const UNIFORM_NAMES = [
  'uBuckets',
  'uTexSize',
  'uBucketCount',
  'uFirstBucket',
  'uVisibleBuckets',
  'uEnvelope',
  'uCore',
  'uPlayhead',
  'uPlayheadX',
  'uPlayheadHalfWidth',
  'uBeatBuckets',
  'uAnchorBucket',
  'uBarBeats',
] as const;

/**
 * The uniforms a GLSL source actually declares.
 *
 * Deliberately a regex over the source rather than a hand-kept second list: a second list is the
 * thing that just went wrong. This one cannot drift from the shader because it *is* the shader.
 */
export function uniformNamesIn(shaderSource: string): string[] {
  const found = new Set<string>();
  const pattern = /^\s*uniform\s+\w+\s+(\w+)\s*;/gm;
  let match = pattern.exec(shaderSource);
  while (match !== null) {
    if (match[1] !== undefined) {
      found.add(match[1]);
    }
    match = pattern.exec(shaderSource);
  }
  return [...found];
}

interface UploadedLevel {
  readonly texture: WebGLTexture;
  readonly level: WaveformMipmapLevel;
  readonly textureWidth: number;
  readonly textureHeight: number;
}

interface LoadedTrack {
  readonly frameCount: number;
  readonly sampleRateHz: number;
  readonly signals: SignalReader;
  /** Last playhead seen, in seconds. Kept because the ring only yields *new* frames: a paused deck
   * publishes the same position forever, but a redraw that arrives between two blocks gets nothing
   * new and must draw the last known position rather than snapping to zero. */
  playheadSeconds: number;
  readonly ribbon: UploadedLevel;
  readonly overview: UploadedLevel;
}

/**
 * Gets a WebGL2 context from either kind of canvas.
 *
 * `RendererInit.canvas` is `HTMLCanvasElement | OffscreenCanvas`, and calling `getContext` across
 * that union collapses the return type to `RenderingContext | null` — TypeScript cannot express
 * "the 'webgl2' id determines the result" through a union of two overload sets. `instanceof` narrows
 * it with a real runtime check rather than an assertion, which also happens to be the honest thing:
 * a browser that hands back something else has told us WebGL2 is unavailable.
 */
function getWebGl2(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  options: WebGLContextAttributes,
): WebGL2RenderingContext | null {
  const context = canvas.getContext('webgl2', options);
  return context instanceof WebGL2RenderingContext ? context : null;
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (shader === null) {
    throw new Error('waveform: could not create shader');
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? 'unknown';
    gl.deleteShader(shader);
    throw new Error(`waveform: shader failed to compile — ${log}`);
  }
  return shader;
}

export class WaveformRenderer implements Renderer {
  readonly #gl: WebGL2RenderingContext;
  readonly #program: WebGLProgram;
  readonly #uniforms: Record<string, WebGLUniformLocation | null>;
  /** The theme as GL wants it. Built once: `uniform3fv` takes a `Float32List`, and converting a
   * readonly tuple to one per frame would allocate three arrays sixty times a second on the exact
   * path this whole file exists to keep allocation-free. */
  readonly #envelope: Float32Array;
  readonly #core: Float32Array;
  readonly #playhead: Float32Array;

  #width: number;
  #height: number;
  #pixelRatio: number;
  #track: LoadedTrack | null = null;
  /** Which deck's block in the FR-26 frame this lane reads (P3.13). Was `DECK_A` for everyone. */
  readonly #sourceIndex: SignalSourceIndex;
  /** Deck A draws mirrored so the two ribbons meet — `WAVEFORM_LAYOUT_MIRRORED`. */
  readonly #layout: WaveformLaneLayout;
  /**
   * The grid to tick, or null (P3.13).
   *
   * Held on the renderer rather than on `#track` because the two have different lifetimes: a track
   * arrives once, and FR-13's tools can move its grid a dozen times without any audio changing.
   * Keeping it here means a correction costs one small message rather than re-uploading the mipmaps.
   */
  #grid: WaveformGrid | null = null;

  constructor(
    init: RendererInit,
    theme: WaveformTheme,
    sourceIndex: SignalSourceIndex = SIGNAL_SOURCE.DECK_A,
    orientation: WaveformOrientation = 'normal',
  ) {
    // `alpha: true` and no depth or stencil: this canvas sits over the glass panel and draws a
    // 2D data display. Asking for buffers nothing uses costs memory on every deck.
    const gl = getWebGl2(init.canvas, {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: false,
      // The waveform is redrawn every frame from scratch; preserving it costs a copy per frame.
      preserveDrawingBuffer: false,
    });

    if (gl === null) {
      throw new Error('waveform: WebGL2 is unavailable on this canvas');
    }

    this.#gl = gl;
    this.#envelope = Float32Array.from(theme.envelope);
    this.#core = Float32Array.from(theme.core);
    this.#playhead = Float32Array.from(theme.playhead);
    this.#width = init.width;
    this.#height = init.height;
    this.#pixelRatio = init.pixelRatio;

    // No null check on `createProgram`/`createShader`/`createTexture` anywhere in this file, and it
    // is the lib's claim rather than an assumption of ours: TypeScript's DOM definitions type these
    // as non-nullable. In a real browser they *can* return null after a context loss, in which case
    // the next call throws a TypeError — which `WaveformLane` catches and turns into a lane with no
    // waveform in it rather than a broken app (PRD §6.3). The failure is handled; it is just handled
    // one level up instead of here.
    const program = gl.createProgram();
    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) ?? 'unknown';
      throw new Error(`waveform: program failed to link — ${log}`);
    }

    this.#program = program;
    this.#uniforms = Object.fromEntries(
      UNIFORM_NAMES.map((name) => [name, gl.getUniformLocation(program, name)]),
    );

    this.#sourceIndex = sourceIndex;
    this.#layout = layoutFor(orientation);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    this.#applyCanvasSize(init.canvas);
  }

  #applyCanvasSize(canvas: HTMLCanvasElement | OffscreenCanvas): void {
    canvas.width = Math.max(1, Math.round(this.#width * this.#pixelRatio));
    canvas.height = Math.max(1, Math.round(this.#height * this.#pixelRatio));
  }

  /**
   * Uploads one mipmap level as a texture.
   *
   * Repacked from the mipmaps' interleaved 3-float buckets into RGBA, and the fourth component is
   * genuinely wasted. It buys the one thing worth paying for here: `RGBA32F` is core WebGL2 with no
   * alignment caveats, while a 3-component float upload depends on `UNPACK_ALIGNMENT` being right
   * and is exactly the kind of thing that works on the machine it was written on. The repack runs
   * once per track load, off every frame budget.
   */
  #upload(mipmaps: SharedArrayBuffer, level: WaveformMipmapLevel): UploadedLevel {
    const gl = this.#gl;
    const texture = gl.createTexture();

    const width = Math.min(MAX_TEXTURE_WIDTH, Math.max(1, level.bucketCount));
    const height = Math.max(1, Math.ceil(level.bucketCount / width));
    const packed = new Float32Array(width * height * 4);
    const source = new Float32Array(
      mipmaps,
      level.byteOffset,
      level.bucketCount * WAVEFORM_BUCKET_FLOATS,
    );

    for (let bucket = 0; bucket < level.bucketCount; bucket += 1) {
      const from = bucket * WAVEFORM_BUCKET_FLOATS;
      const to = bucket * 4;
      packed[to] = source[from] ?? 0;
      packed[to + 1] = source[from + 1] ?? 0;
      packed[to + 2] = source[from + 2] ?? 0;
    }

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA32F,
      width,
      height,
      0,
      gl.RGBA,
      gl.FLOAT,
      packed,
    );
    // NEAREST throughout. A bucket is a measurement, not a sample of a continuous function —
    // interpolating between two of them invents a peak that is not in the audio. It also means
    // `OES_texture_float_linear`, which is an extension, is never needed.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    return { texture, level, textureWidth: width, textureHeight: height };
  }

  /** A track finished analysis. Chooses one level per display and uploads both, once. */
  setTrack(
    mipmaps: SharedArrayBuffer,
    signals: SharedArrayBuffer,
    frameCount: number,
    sampleRateHz: number,
  ): void {
    this.clearTrack();

    const layout = computeWaveformMipmapLayout({ frameCount, sampleRateHz });
    const devicePixels = Math.max(1, this.#width * this.#pixelRatio);

    // The ribbon shows a fixed span, so its frames-per-pixel is fixed too.
    const ribbonLevel = levelForFramesPerPixel(
      layout,
      (RIBBON_VISIBLE_SECONDS * sampleRateHz) / devicePixels,
    );
    // The overview shows the whole track across the same width.
    const overviewLevel = levelForFramesPerPixel(layout, frameCount / devicePixels);

    this.#track = {
      frameCount,
      sampleRateHz,
      signals: new SignalReader(signals),
      playheadSeconds: 0,
      ribbon: this.#upload(mipmaps, ribbonLevel),
      overview: this.#upload(mipmaps, overviewLevel),
    };
  }

  clearTrack(): void {
    const track = this.#track;
    if (track === null) {
      return;
    }
    this.#gl.deleteTexture(track.ribbon.texture);
    this.#gl.deleteTexture(track.overview.texture);
    this.#track = null;
  }

  resize(width: number, height: number, pixelRatio: number): void {
    this.#width = width;
    this.#height = height;
    this.#pixelRatio = pixelRatio;
    const gl = this.#gl;
    gl.canvas.width = Math.max(1, Math.round(width * pixelRatio));
    gl.canvas.height = Math.max(1, Math.round(height * pixelRatio));
  }

  #drawRegion(
    uploaded: UploadedLevel,
    topFraction: number,
    heightFraction: number,
    firstBucket: number,
    visibleBuckets: number,
    playheadX: number,
    withTicks: boolean,
  ): void {
    const gl = this.#gl;
    const pixelHeight = gl.drawingBufferHeight;
    const pixelWidth = gl.drawingBufferWidth;

    // y is measured from the bottom in GL and from the top in the layout constants.
    const height = Math.max(1, Math.round(heightFraction * pixelHeight));
    const top = Math.round(topFraction * pixelHeight);
    gl.viewport(0, pixelHeight - top - height, pixelWidth, height);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, uploaded.texture);
    gl.uniform1i(this.#uniforms['uBuckets'] ?? null, 0);
    gl.uniform2i(
      this.#uniforms['uTexSize'] ?? null,
      uploaded.textureWidth,
      uploaded.textureHeight,
    );
    gl.uniform1i(this.#uniforms['uBucketCount'] ?? null, uploaded.level.bucketCount);
    gl.uniform1f(this.#uniforms['uFirstBucket'] ?? null, firstBucket);
    gl.uniform1f(this.#uniforms['uVisibleBuckets'] ?? null, visibleBuckets);
    gl.uniform1f(this.#uniforms['uPlayheadX'] ?? null, playheadX);
    // A playhead one device pixel wide, expressed in the 0..1 the shader compares against.
    gl.uniform1f(
      this.#uniforms['uPlayheadHalfWidth'] ?? null,
      1 / Math.max(1, pixelWidth),
    );

    // The grid, converted to this level's bucket units so the fragment shader does no time
    // arithmetic. A zero period is the "no grid" signal the shader tests for — and it is also what
    // a corrupt grid or a missing track degrades to, rather than a division producing infinities
    // across the whole lane.
    const track = this.#track;
    const grid = this.#grid;
    const bucketSeconds =
      track === null || track.sampleRateHz <= 0
        ? 0
        : uploaded.level.bucketFrames / track.sampleRateHz;
    const ticking =
      withTicks && grid !== null && grid.beatSeconds > 0 && bucketSeconds > 0;

    gl.uniform1f(
      this.#uniforms['uBeatBuckets'] ?? null,
      ticking ? grid.beatSeconds / bucketSeconds : 0,
    );
    gl.uniform1f(
      this.#uniforms['uAnchorBucket'] ?? null,
      ticking ? grid.anchorSeconds / bucketSeconds : 0,
    );
    gl.uniform1f(
      this.#uniforms['uBarBeats'] ?? null,
      ticking && grid.showBars ? BEATS_PER_BAR : 0,
    );

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /**
   * One frame. `deltaSeconds` is deliberately unused.
   *
   * Everything this draws is a function of the playhead, and the playhead comes from the **audio
   * thread** via `deckStatus` — Architecture §7: the UI "renders these; it never computes transport
   * time from its own clocks". Integrating a rAF delta here would produce a playhead that looked
   * right and drifted against the audio, which is the class of bug §7 exists to make impossible.
   */
  frame(): void {
    const gl = this.#gl;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const track = this.#track;
    if (track === null) {
      return;
    }

    gl.useProgram(this.#program);
    gl.uniform3fv(this.#uniforms['uEnvelope'] ?? null, this.#envelope);
    gl.uniform3fv(this.#uniforms['uCore'] ?? null, this.#core);
    gl.uniform3fv(this.#uniforms['uPlayhead'] ?? null, this.#playhead);

    // Newest-wins, not peak: a position is not a level, and taking a maximum across the interval
    // would run the playhead ahead of the audio whenever a frame arrived late. `null` means no new
    // frame since the last draw — a paused deck, or a redraw landing between blocks — so the last
    // known position is drawn rather than snapping to zero.
    const published = track.signals.readLatestPlayhead(this.#sourceIndex);
    if (published !== null) {
      track.playheadSeconds = published;
    }
    const positionFrames = track.playheadSeconds * track.sampleRateHz;

    const ribbonBuckets =
      (RIBBON_VISIBLE_SECONDS * track.sampleRateHz) / track.ribbon.level.bucketFrames;
    const centreBucket = positionFrames / track.ribbon.level.bucketFrames;

    this.#drawRegion(
      track.ribbon,
      this.#layout.RIBBON_TOP,
      this.#layout.RIBBON_HEIGHT,
      centreBucket - ribbonBuckets / 2,
      ribbonBuckets,
      // The ribbon's playhead never moves: the waveform scrolls past it. That is what makes the
      // centre line a fixed reference a DJ can aim at.
      0.5,
      // Ticks on the ribbon only. On the overview a whole track's beats are thousands of lines
      // inside a few hundred pixels, which is a solid block rather than a grid.
      true,
    );

    this.#drawRegion(
      track.overview,
      this.#layout.OVERVIEW_TOP,
      this.#layout.OVERVIEW_HEIGHT,
      0,
      track.overview.level.bucketCount,
      track.frameCount === 0 ? -1 : positionFrames / track.frameCount,
      false,
    );
  }

  /** FR-13's grid changed, or a new track arrived with one. Null draws no ticks. */
  setGrid(grid: WaveformGrid | null): void {
    this.#grid = grid;
  }

  dispose(): void {
    this.clearTrack();
    this.#gl.deleteProgram(this.#program);
    // Frees the backing drawing buffer immediately rather than at GC's convenience. A leaked GL
    // context outlives the page it was created for.
    this.#gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
